import React, { useState } from 'react';
import { Opportunity } from '../types/api';
import opportunityService from '../services/opportunityService';

const NAME_MAX_LENGTH = 50;

interface NewOpportunityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (opportunity: Opportunity) => void;
}

/**
 * Form to create a new opportunity - a PBS-local project used to model work that
 * does not exist in Redmine yet. On success the created project is handed back so
 * the caller can select it and move straight on to picking team members.
 */
const NewOpportunityModal: React.FC<NewOpportunityModalProps> = ({ isOpen, onClose, onCreated }) => {
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);

  const resetForm = () => {
    setName('');
    setDescription('');
    setError(null);
    setSaving(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Project title is required');
      return;
    }
    if (trimmedName.length > NAME_MAX_LENGTH) {
      setError(`Project title must be ${NAME_MAX_LENGTH} characters or fewer`);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const created = await opportunityService.createOpportunity({
        name: trimmedName,
        description: description.trim(),
      });
      resetForm();
      onCreated(created);
    } catch (err: any) {
      setError(err?.message || 'Failed to create opportunity');
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="new-opportunity-title"
      aria-describedby="new-opportunity-description"
    >
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-white">
        {/* Modal Header */}
        <div className="flex justify-between items-center mb-4">
          <h3 id="new-opportunity-title" className="text-lg font-medium text-gray-900">
            New Opportunity
          </h3>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close modal"
            className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-400 rounded p-1 transition-all"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <p id="new-opportunity-description" className="text-sm text-gray-600 mb-4">
          Create a project for prospective work so you can model its team and costs.
        </p>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md" role="alert" aria-live="polite">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800 font-medium">{error}</p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Project title */}
          <div className="mb-4">
            <div className="flex justify-between items-baseline">
              <label htmlFor="opportunity-name" className="block text-sm font-medium text-gray-700 mb-1">
                Project title *
              </label>
              <span className="text-xs text-gray-500" aria-hidden="true">
                {name.length}/{NAME_MAX_LENGTH}
              </span>
            </div>
            <input
              id="opportunity-name"
              type="text"
              required
              autoFocus
              maxLength={NAME_MAX_LENGTH}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Acme Platform Rebuild"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
            />
          </div>

          {/* Description */}
          <div className="mb-6">
            <label htmlFor="opportunity-description" className="block text-sm font-medium text-gray-700 mb-1">
              Description <span className="text-gray-400">(optional)</span>
            </label>
            <textarea
              id="opportunity-description"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Scope, client, expected duration..."
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
            />
          </div>

          {/* Footer */}
          <div className="flex space-x-3">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {saving ? 'Creating...' : 'Create & Add Members'}
            </button>
            <button
              type="button"
              onClick={handleClose}
              disabled={saving}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-1 disabled:opacity-50 transition-all"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewOpportunityModal;
